import { useEffect, useRef, useState } from "react";
import { Mail, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

interface Props {
  email: string;
  isSelf: boolean;
  confirming: boolean;
  onRequestConfirm: () => void;
  onCancel: () => void;
  onRemove: () => Promise<void>;
}

export function UserRow({
  email,
  isSelf,
  confirming,
  onRequestConfirm,
  onCancel,
  onRemove,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [removed, setRemoved] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!confirming) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    const onClickOutside = (e: MouseEvent) => {
      if (rowRef.current && !rowRef.current.contains(e.target as Node)) onCancel();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [confirming, onCancel]);

  const handleRemove = async () => {
    setBusy(true);
    try {
      await onRemove();
      setRemoved(true);
    } catch {
      setBusy(false);
    }
  };

  return (
    <div
      ref={rowRef}
      className={cn(
        "flex items-center gap-3 px-1 py-3 transition-all duration-150",
        busy && "opacity-50",
        removed && "max-h-0 overflow-hidden py-0 opacity-0",
      )}
    >
      <Mail className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
      <span className="flex-1 truncate font-mono text-sm text-slate-100">{email}</span>
      {isSelf && !confirming && (
        <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
          you
        </span>
      )}

      {confirming ? (
        <div className="flex items-center gap-2">
          {isSelf ? (
            <span className="hidden text-xs text-amber-400 sm:inline">
              You will lose access immediately.
            </span>
          ) : (
            <span className="text-xs text-slate-400">Remove?</span>
          )}
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleRemove}
            disabled={busy}
            className={isSelf ? "bg-red-600 text-slate-50 hover:bg-red-500" : undefined}
          >
            {busy ? "Removing…" : "Remove"}
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onRequestConfirm}
          aria-label={`Remove ${email}`}
          className="rounded p-1 text-slate-500 transition-colors hover:bg-red-950/40 hover:text-red-400"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
